import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

export type SpaceAction = Action.SpaceAdmin | Action.SpaceWrite | Action.SpaceRead;

const LEVELS_ORDER = {
  [Action.SpaceAdmin]: 2,
  [Action.SpaceWrite]: 1,
  [Action.SpaceRead]: 0,
};

/**
 * Returns all actions that have permissions greater than or equal to the required action.
 * The permission hierarchy is: Admin > Write > Read
 *
 * @param requiredAction - The minimum required action
 * @returns An array of actions with permissions greater than or equal to the required action
 *
 * @public (Knip ignored - used in the file and exported for testing)
 */
export const getActionsWithGreaterOrEqualPermissions = (
  requiredAction: SpaceAction
): SpaceAction[] => {
  const requiredLevel = LEVELS_ORDER[requiredAction];

  // Define all possible space actions
  const allActions: SpaceAction[] = [Action.SpaceAdmin, Action.SpaceWrite, Action.SpaceRead];

  // Filter actions based on their required access level
  return allActions.filter((action) => {
    const actionLevel = LEVELS_ORDER[action];

    if (actionLevel >= requiredLevel) {
      return true;
    }

    return false;
  });
};

/**
 * Hook that checks if a user has access to perform a specific action on a stack.
 *
 * @param requiredAction - The action to check access for
 * @returns A function that takes a stack and returns whether the user has access to perform the action
 */
const useHasStackActionAccess = (
  requiredAction: Action.SpaceAdmin | Action.SpaceWrite | Action.SpaceRead
): ((stack?: Stack) => boolean) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack?: Stack) => {
      if (!stack) {
        return false;
      }

      // Admin users have access to all actions
      if (viewer.admin) {
        return true;
      }

      return hasAtLeastOneRequiredAction(
        getActionsWithGreaterOrEqualPermissions(requiredAction),
        viewer,
        stack.spaceDetails?.id || stack.space
      );
    },
    [requiredAction, viewer]
  );
};

export default useHasStackActionAccess;
